<script lang="ts">import TooltipInfo from "./tooltip-info.svelte";
import { Card } from "@infinitro/card";
import { toGameSlug, toProviderSlug } from "@infinitro/catalog-min";
import { formatBaseCurrency, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { displayInFiat, formatCurrencyString, formatNumber, isMobile, loggedIn, ModalDialog, selectedBetSlip, selectedProfile, stateOpenModal, truncateText } from "@infinitro/global";
import { CurrencyIcon } from "@infinitro/icons";
import { Text } from "@infinitro/text";
const formatBetFeedAmount = (amount, currency, inFiat) => {
	const value = Number(amount);
	if (isNaN(value)) return "";
	if (useCompactRows) {
		// In compact mode: hide decimals, just show the whole number
		return Math.floor(value).toString();
	}
	if (inFiat) {
		return formatBaseCurrency(value, false, 0, true);
	}
	return formatCurrencyString(value, currency);
};
const formatMultiplier = (multiplier) => {
	if (useCompactRows) {
		const wholeNumber = Math.floor(multiplier).toString();
		return `${wholeNumber}X`;
	}
	return `${formatNumber(multiplier, false, true)}X`;
};
export let bets = [];
export const openUserInfoModal = (username) => {
	if (username === undefined || username === "" || !$loggedIn) {
		return;
	}
	selectedProfile.set(username);
	stateOpenModal.set(ModalDialog.OtherProfile);
};
export const openBetSlipModal = (bet) => {
	if (!bet) {
		return;
	}
	selectedBetSlip.set(bet);
	stateOpenModal.set(ModalDialog.BetSlip);
};
let last = undefined;
$: sortedBets = [...bets].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
$: {
	const newLast = sortedBets.length > 0 ? sortedBets[0].createdAt : undefined;
	if (newLast !== last) {
		last = newLast;
	}
}
$: useAlternative = $stateUi.table.tableStyle === "alternative" || $stateUi.table.tableStyle === "alternative2";
$: useAlternative4 = $stateUi.table.tableStyle === "alternative4";
$: useCompactRows = $stateUi.betfeed.rowDensity === "compact";
</script>

<div class="bet-feed-table-mobile-container">
	<table class="bet-feed-table-mobile" class:bet-feed-table-mobile-compact={useCompactRows}>
		<thead>
			<tr>
				<td class="mobile-game-header">
					<span class="truncated-text">
						{$t(Verbiage.game)}
					</span>
				</td>

				<td class="mobile-user-header">
					<span class="truncated-text">
						{$t(Verbiage.user)}
					</span>
				</td>

				<td class="mobile-bet-header">
					<span class="truncated-text">
						{$t(Verbiage.bet)}
					</span>
				</td>

				<td class="mobile-multiplier-header" class:compact-multiplier={useCompactRows}>
					<span class="truncated-text">
						{$t(Verbiage.multiplier)}
					</span>
				</td>

				<td class="mobile-payout-header" class:compact-payout={useCompactRows}>
					<span class="truncated-text">
						{$t(Verbiage.payout)}
					</span>
				</td>
			</tr>
		</thead>

		<tbody class="bet-feed-table-mobile-body">
			{#each bets as bet, index (`${bet.betRoundId ?? bet.createdAt}-${index}`)}
				<tr
					class="bet-feed-table-mobile-row"
					class:alternative-bet-feed-table-mobile-row={useAlternative}
					class:alternative4-bet-feed-table-mobile-row={useAlternative4}
					class:compact-bet-feed-table-mobile-row={useCompactRows}
				>
					<td class="mobile-game-column">
						<button
							class="game-name-text"
							id={`img${bet.betRoundId}`}
							on:click={() => openBetSlipModal(bet)}
						>
							{#if !useCompactRows}
								<Card
									width={$isMobile ? '12' : undefined}
									item={{
										game: bet.gameName.toLowerCase(),
										prov: bet.providerSlug.toLowerCase(),
										tile: `t/${toProviderSlug(bet.providerName)}/${toGameSlug(bet.gameName)}`,
										alt: `${bet.gameName} by ${bet.providerName}`
									}}
									thumbnail
									showOverlay={false}
								/>
							{/if}
							<span class="truncated-text game-name">
								{bet.gameName}
							</span>
						</button>
					</td>

					<td class="mobile-user-column">
						<button
							class="user-button truncated-text"
							on:click={() => openUserInfoModal(bet.username)}
						>
							<span class="username-text truncated-text">
								{bet.username || `${$t(Verbiage.hidden)}`}
							</span>
						</button>
					</td>

					<td class="mobile-bet-column">
						<div class="bet">
							<span class="truncated-text bet-text">
								<span class="bet-value-mobile">
									{#if $isMobile}
										<TooltipInfo
											text={formatBetFeedAmount(
												$displayInFiat ? bet.wagerTotalUsd : bet.wagerTotal,
												bet.currency,
												$displayInFiat
											)}
											currency={bet.currency}
										>
											{truncateText(
												formatBetFeedAmount(
													$displayInFiat ? bet.wagerTotalUsd : bet.wagerTotal,
													bet.currency,
													$displayInFiat
												),
												6
											)}
										</TooltipInfo>
									{/if}
								</span>
								<span class="bet-value">
									{formatBetFeedAmount(
										$displayInFiat ? bet.wagerTotalUsd : bet.wagerTotal,
										bet.currency,
										$displayInFiat
									)}
								</span>
							</span>
							{#if !useCompactRows && !$displayInFiat}
								<CurrencyIcon currency={bet.currency} size={$isMobile ? '16' : '25'} />
							{/if}
						</div>
					</td>

					<td class="mobile-multiplier-column" class:compact-multiplier={useCompactRows}>
						<span class="truncated-text">
							{formatMultiplier(bet.multiplier)}
						</span>
					</td>

					<td class="mobile-payout-column" class:compact-payout={useCompactRows}>
						<div class="payout">
							<span class="truncated-text payout-text">
								<span class="payout-value-mobile truncated-text">
									{#if $isMobile}
										<TooltipInfo
											text={formatBetFeedAmount(
												$displayInFiat ? bet.paidTotalUsd : bet.paidTotal,
												bet.currency,
												$displayInFiat
											)}
											currency={bet.currency}
											textColor={parseFloat(
												`${$displayInFiat ? bet.paidTotalUsd : bet.paidTotal}`
											) > 0
												? 'success'
												: parseFloat(`${$displayInFiat ? bet.paidTotalUsd : bet.paidTotal}`) < 0
													? 'error'
													: 'gray'}
										>
											<Text
												textColor={parseFloat(
													`${$displayInFiat ? bet.paidTotalUsd : bet.paidTotal}`
												) > 0
													? 'success'
													: parseFloat(`${$displayInFiat ? bet.paidTotalUsd : bet.paidTotal}`) < 0
														? 'error'
														: 'gray'}
											>
												{truncateText(
													formatBetFeedAmount(
														$displayInFiat ? bet.paidTotalUsd : bet.paidTotal,
														bet.currency,
														$displayInFiat
													),
													6
												)}
											</Text>
										</TooltipInfo>
									{/if}
								</span>

								<span class="payout-value">
									<Text
										textColor={parseFloat(`${$displayInFiat ? bet.paidTotalUsd : bet.paidTotal}`) >
										0
											? 'success'
											: parseFloat(`${$displayInFiat ? bet.paidTotalUsd : bet.paidTotal}`) < 0
												? 'error'
												: 'gray'}
									>
										{formatBetFeedAmount(
											$displayInFiat ? bet.paidTotalUsd : bet.paidTotal,
											bet.currency,
											$displayInFiat
										)}
									</Text>
								</span>
							</span>
							{#if !useCompactRows && !$displayInFiat}
								<CurrencyIcon currency={bet.currency} size={$isMobile ? '16' : '25'} />
							{/if}
						</div>
					</td>
				</tr>
			{/each}
		</tbody>
	</table>
</div>

<style>
	.bet-feed-table-mobile-container {
		position: relative;
		overflow: hidden;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.bet-feed-table-mobile {
		display: table;
		width: 100%;
		border-collapse: collapse;
		border-color: inherit;
		overflow-anchor: none;
		table-layout: fixed;
		text-indent: 0;
}

	.bet-feed-table-mobile-body,
	.bet-feed-table-mobile-row,
	.bet-feed-table-mobile-row td {
		position: relative;
		overflow: visible;
}

	.bet-feed-table-mobile-row {
		border-radius: 0.5rem;
}

	.bet-feed-table-mobile-row:nth-child(odd) {
		background-color: color-mix(in srgb,var(--component-table-odd-row) 50%,transparent);
}

	.alternative-bet-feed-table-mobile-row:nth-child(odd) {
		background-color: var(--component-table-odd-row);
}

	.bet-feed-table-mobile-row.compact-bet-feed-table-mobile-row:nth-child(odd) {
		background-color: var(--color-background-500);
}

	.compact-bet-feed-table-mobile-row {
		height: 24px;
	}
	.compact-bet-feed-table-mobile-row td {
		height: 24px;
		padding-top: 0px;
		padding-bottom: 0px;
		vertical-align: middle;
		font-size: 12px;
		line-height: 12px;
	}

	.compact-bet-feed-table-mobile-row .game-name {
		margin-left: 0px;
}
	.bet {
		display: flex;
		align-items: center;
		justify-content: flex-end;
}

	.bet-text,
	.payout-text {
		margin-right: 0.25rem;
}

	.bet-value {
		display: none;
}

	.bet-value-mobile {
		display: block;
}

	.game-name {
		margin-left: 0.5rem;
}

	.game-name-text {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: flex-start;
		text-overflow: ellipsis;
}

	.game-name-text:hover {
		text-decoration-line: underline;
}

	.mobile-bet-column {
		padding-left: 0px;
		padding-right: 0px;
		text-align: end;
}

	.mobile-bet-header {
		text-align: center;
		opacity: 0.4;
}

	.mobile-game-column {
		width: auto;
		border-top-left-radius: 0.5rem;
		border-bottom-left-radius: 0.5rem;
		padding-right: 0px;
		text-align: start;
}

	.mobile-game-header {
		width: auto;
		text-align: start;
		opacity: 0.4;
}

	.mobile-multiplier-column {
		width: 3.9rem;
		padding-left: 0px;
		padding-right: 0px;
		text-align: center;
}

	.mobile-multiplier-header {
		width: 3.9rem;
		padding-left: 0px;
		padding-right: 0px;
		text-align: center;
		opacity: 0.4;
}

	.mobile-multiplier-column.compact-multiplier,
	.mobile-multiplier-header.compact-multiplier {
		width: 3rem;
}

	.mobile-payout-column {
		border-top-right-radius: 0.5rem;
		border-bottom-right-radius: 0.5rem;
		padding-left: 0px;
		padding-right: 0px;
		padding-right: 0.75rem;
		text-align: end;
}

	.mobile-payout-header {
		text-align: right;
		opacity: 0.4;
}

	.mobile-payout-column.compact-payout,
	.mobile-payout-header.compact-payout {
		width: 5.5rem;
}

	.mobile-user-column {
		display: none;
}

	.mobile-user-header {
		display: none;
		opacity: 0.4;
}

	.payout {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: flex-end;
}

	.alternative4-bet-feed-table-mobile-row .payout :global(.custom-text-success),
	.alternative4-bet-feed-table-mobile-row .payout :global(.custom-text-success-warm) {
		color: var(--color-success-700);
	}

	.payout-value {
		display: none;
}

	.payout-value-mobile {
		display: block;
}

	td {
		padding: 0.75rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
}

	@media (min-width: 640px) {

		td {
				padding-left: 1rem;
				padding-right: 1rem;
		}
}

	.bet-feed-table-mobile-compact thead td {
		font-size: 12px;
		line-height: 12px;
}

	.bet-feed-table-mobile-compact .mobile-bet-column {
		text-align: center;
}

	.bet-feed-table-mobile-compact .mobile-bet-header {
		text-align: center;
}

	.bet-feed-table-mobile-compact .bet {
		justify-content: center;
}

	.truncated-text {
		display: block;
		max-width: 98%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
}

	@media (min-width: 768px) {
		.mobile-bet-column,
		.mobile-bet-header {
				margin-left: 0.75rem;
				margin-right: 0.75rem;
		}
	}

	@media (min-width: 420px) {
		.mobile-bet-header {
				padding-left: 0px;
				padding-right: 0px;
				text-align: end;
		}
	}

	@media (min-width: 650px) {
		.mobile-multiplier-column,
		.mobile-multiplier-header {
				width: auto;
				text-align: end;
		}

		.bet-feed-table-mobile-compact .mobile-multiplier-column,
		.bet-feed-table-mobile-compact .mobile-multiplier-header {
				text-align: center;
		}

		.bet-feed-table-mobile-compact .mobile-bet-column {
				text-align: start;
		}

		.bet-feed-table-mobile-compact .mobile-bet-header {
				text-align: start;
		}

		.bet-feed-table-mobile-compact .bet {
				justify-content: flex-start;
		}

		.user-button {
				display: flex;
				width: 100%;
				align-items: center;
				justify-content: flex-start;
				text-align: start;
		}

		.user-button:hover {
				text-decoration-line: underline;
		}

		.mobile-user-column,
		.mobile-user-header {
				display: flex;
				align-items: center;
				justify-content: flex-start;
				padding-right: 0px;
				text-align: start;
		}

		.bet-feed-table-mobile-compact .mobile-user-column {
				align-items: stretch;
		}

		.bet-feed-table-mobile-compact .user-button {
				min-height: 100%;
		}
	}

	@media (min-width: 1024px) {
		.payout-value-mobile,
		.bet-value-mobile {
				display: none;
		}

		.payout-value,
		.bet-value {
				display: block;
		}
	}
</style>
